/**
 * Splitting food-name text into words, WITHOUT stemming it.
 *
 * This is the first half of the analyzer chain the food search runs. The second half — the
 * possessive stemmer and the English stemmer — lives in `analyzeFoodText` in the client's
 * `food-search-match.ts`, which calls this and then stems what comes back.
 *
 * WHY THE SPLIT EXISTS. Two callers need words at different stages of that chain:
 *
 * - MATCHING needs stemmed words, because that is what the Elasticsearch index stores. Both
 *   "tomato" and "tomatoes" have to reach the same term or a search for one misses the other.
 * - The WORD DICTIONARY that completes what the user is typing needs the words UNSTEMMED,
 *   because it is shown to a person. "Chocolate" stems to "chocol", and offering somebody
 *   "chocol" as a completion of "choc" would be nonsense (#5319).
 *
 * WHY IT LIVES IN `shared/` RATHER THAN BESIDE THE MATCHER. The dictionary is built by a
 * command-line tool (the `build-food-word-dictionary` command in `tools/src/elastic-cmd.ts`,
 * whose counting lives in `tools/src/food-word-dictionary.ts`) that reads the food index, and
 * the app then completes words against what that tool produced. If the two split text into
 * words even slightly differently, the tool records words the app can never look up. Sharing
 * one function is what stops that drifting apart — the same reasoning that made the matcher a
 * documented replica of the index rather than a second guess at it.
 *
 * `foldAccents` is passed in rather than imported so this module has no dependencies and loads
 * anywhere: the app hands it `transliterate` from the `transliteration` package, and so does
 * the tool. Same convention as `food-search-match.ts` and `food-search-elastic.ts`.
 */

/**
 * The words in `text`, lowercased and with accents folded, in the order they appear.
 *
 * Approximates Elasticsearch's `standard` tokenizer: a word is a run of letters, digits and
 * apostrophes. A possessive ending is dropped, so "grandma's" and "grandma" are one word —
 * that mirrors the index's `english_possessive_stemmer`, and it is here rather than with the
 * stemmers because it changes the word a PERSON would recognise, not just its stem.
 *
 * Accent folding is ours alone and is a knowing difference from the index, which has no
 * `asciifolding` filter: this turns "crème" into "creme" where the index keeps "crème". See
 * the divergence table in `design-docs/elasticsearch-infrastructure.md`.
 */
export function tokenizeFoodText(text: string, foldAccents: (text: string) => string): string[] {
  return foldAccents(text)
    .toLowerCase()
    // a word is a run of letters, digits and apostrophes; anything else separates words
    .split(/[^a-z0-9']+/)
    .filter(word => word.length > 0)
    // english_possessive_stemmer: "grandma's" and "grandma" are the same word
    .map(word => word.replace(/'s$/, ''))
    // dropping the possessive can empty a word that was nothing but "'s"
    .filter(word => word.length > 0);
}

/**
 * A possessive ending, in each of the three apostrophes the word-break rules keep inside a word.
 *
 * Those three are U+0027 "'", U+2019 "’" (what an iPhone types by default) and U+2018 "‘". They
 * are the ones Elasticsearch's `standard` tokenizer holds inside a word, measured against the dev
 * cluster on 2026-09-10; two lookalikes it splits on instead are U+201B "‛" and U+2032 "′".
 */
const RAW_POSSESSIVE_ENDING = /['’‘]s$/;

/**
 * The word segmenter, built on first use rather than when this module loads.
 *
 * Deliberately lazy. This module is also imported by the CLIENT, and `Intl.Segmenter` is missing
 * from Firefox before version 125. Built at load time, it would throw on import in such a browser
 * and take the whole food search down with it, rather than costing only the one call that needs
 * segmenting.
 *
 * The client calls `splitFoodTextRaw` too since #5410 — `food-search-correction.ts` compares a
 * committed search with a suggestion row using the same splitter the server sends to
 * Elasticsearch — so this is no longer a server-only function. That does not change the reasoning:
 * no browser the app supports is missing `Intl.Segmenter` (`last 2 Firefox versions`), and staying
 * lazy keeps the failure, if one ever arrives, at the call rather than at the import.
 */
let wordSegmenter: Intl.Segmenter | undefined;

function getWordSegmenter(): Intl.Segmenter {
  // a fixed locale, so the answer never depends on the machine it happens to run on
  wordSegmenter ??= new Intl.Segmenter('en', { granularity: 'word' });
  return wordSegmenter;
}

/**
 * The words in `text` as they are WRITTEN — lowercased, but with their accents left alone.
 *
 * WHY THIS EXISTS BESIDE `tokenizeFoodText` RATHER THAN REPLACING IT. The two answer different
 * questions and both are needed (#5410).
 *
 * `tokenizeFoodText` folds accents BEFORE it splits. That folding is its own design choice — the
 * food index itself keeps accents, having no `asciifolding` filter — and the client's word
 * dictionary was built with it, so it stays as it is. But the ordering makes it structurally unable
 * to hand back an accented word: fold first and "crème" has already become "creme" by the time
 * anything is split, and asking it not to fold is worse than useless — the "è" is then not a letter
 * to it, so the word breaks into "cr" and "me".
 *
 * The phrase index needs the opposite. Each phrase it stores is SHOWN to somebody, and its count is
 * the number of foods whose name holds those words — a count computed on folded text would not
 * match what the un-folded index then finds. So "crème fraîche" has to stay "crème fraîche".
 *
 * HOW IT FINDS WORDS: it does not decide for itself. It asks `Intl.Segmenter`, the platform's
 * implementation of the Unicode word-break rules (UAX #29) — the same rules Elasticsearch's
 * `standard` tokenizer implements. The rules are far subtler than they look, which is why this is
 * not a hand-written regex: an earlier draft had one, and measured against the dev cluster it was
 * wrong on six classes of input. An apostrophe joins two letters ("rock'n'roll") but not a digit
 * and a letter ("90's" is two words), and never sits at a word's edge ("'tis" is "tis"). Of the
 * quote marks that look alike, three stay inside a word and two split it. A soft hyphen stays
 * INSIDE a word. A fraction like "½" and a superscript like "²" are not words at all.
 *
 * On WHICH WORDS a text holds, the segmenter and Elasticsearch agreed on 31 of the 32 cases probed
 * against the dev cluster on 2026-09-10 with `_analyze`; where they disagree is recorded in "Where
 * the two splitters differ" in `design-docs/food-search-suggestions.md`. They differ in two ways,
 * both accepted:
 *
 * - Japanese and Chinese. The segmenter reads a run of ideographs as dictionary words, where
 *   Elasticsearch makes each ideograph a word of its own, so a stored phrase keeps such a run whole.
 * - Symbols. Elasticsearch also emits certain symbols as tokens of their own — "Coke®" gives
 *   "Coke" and "®", "naïve™" gives "naïve" and "™", and emoji belong to the same Unicode class —
 *   while the segmenter does not count them as words at all. That suits a stored phrase, which is
 *   better without them. It does mean a CALLER must not hand Elasticsearch raw typed text where
 *   these words were expected: see Step 10 of the plan.
 *
 * In both cases typing still finds the phrase, because Elasticsearch analyzes the stored phrase's
 * text itself.
 *
 * The cost of two functions is that they disagree, and the disagreement is written down rather
 * than discovered later: `client/src/services/food-text-splitting-divergence.test.ts` pins it for
 * BOTH functions with the real `transliterate`, and step 3 of plan #5410 copies the table into
 * `design-docs/food-search-suggestions.md`. The differences all come from transliteration not
 * being a character-for-character substitution:
 *
 * - A symbol that transliterates into letters. "Coke®" folds to "coke", "r", but gives "coke" here,
 *   because "®" is not word-like. Elasticsearch never produces an "r" for it either.
 * - An input transliteration EXPANDS. "½" folds to "1/2", two words, where this gives none —
 *   agreeing with Elasticsearch, which drops it too.
 *
 * There is deliberately no `foldAccents` parameter. Taking one would invite a caller to pass
 * `transliterate` and quietly recreate the very ordering this function exists to avoid.
 */
export function splitFoodTextRaw(text: string): string[] {
  const words: string[] = [];
  // segment the text as WRITTEN and lowercase each word afterwards -- the order Elasticsearch
  // uses, a tokenizer followed by a lowercase filter
  for (const segment of getWordSegmenter().segment(text)) {
    // punctuation, spaces and symbols come back as segments too; only the word-like ones count
    if (!segment.isWordLike) {
      continue;
    }
    // the same possessive rule `tokenizeFoodText` applies, for the same reason: it changes the word
    // a PERSON would recognise, so "grandma's" and "grandma" are one phrase rather than two
    const word = segment.segment.toLowerCase().replace(RAW_POSSESSIVE_ENDING, '');
    // if the possessive strip left nothing, skip it -- which cannot happen under today's word-break
    // rules, since they never leave an apostrophe at a word's start. kept because those rules come
    // from the platform rather than this file, and an empty word would become an empty phrase
    if (word.length > 0) {
      words.push(word);
    }
  }
  return words;
}

/**
 * Has the person finished typing the last word of `text`?
 *
 * The food suggestion route (#5410) treats an unfinished last word as the start of a word
 * ("chicken brea" may become "chicken breast") and a finished one as a whole word, after which it
 * offers longer phrases ("chicken breast " may become "chicken breast fillets").
 *
 * The answer comes from the same word segmenter `splitFoodTextRaw` uses: the last word is finished
 * when anything that is not part of a word follows it. So a trailing space, a comma and a symbol all
 * finish it alike. A test for a trailing space alone would read "chicken," as unfinished, and the
 * word-break rules are subtler than any hand-written character list (see `splitFoodTextRaw`).
 *
 * Text with no word in it has no last word to finish, so the answer is false.
 */
export function isLastWordFinished(text: string): boolean {
  let lastWasWordLike: boolean | undefined;
  for (const segment of getWordSegmenter().segment(text)) {
    lastWasWordLike = segment.isWordLike === true;
  }
  // if there was no segment at all, or no word anywhere, nothing can be finished
  if (lastWasWordLike === undefined || splitFoodTextRaw(text).length === 0) {
    return false;
  }
  return !lastWasWordLike;
}

/**
 * Is this a word worth offering somebody as a completion?
 *
 * The food database is contributed by users and importers, so its names carry a lot that is
 * not a word: pack sizes ("500", "12"), codes, and single stray letters. A completion list is
 * short and every wasted row costs a real suggestion, so anything with no letter in it is
 * dropped.
 *
 * This deliberately does NOT judge whether a word is spelled correctly or is a real English
 * word. Brand names are neither and are exactly what people search for. Frequency is what
 * separates a real name from a typo, and the dictionary build applies that separately.
 */
export function isCompletableWord(word: string): boolean {
  return /[a-z]/.test(word);
}
